import React from "react";
import {
  Tabs,
  Tab,
  Chip,
  Box,
  useMediaQuery,
  Theme,
  Typography,
} from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";

interface Status {
  label: string;
  name: string;
  total: number;
  color?: string;
  bg?: string;
}

interface Props {
  statuses: Status[];
  selectedStatus: string | null;
  orderTotal: number;
  handleTabChange: (event: React.SyntheticEvent, newValue: string) => void;
}

const StatusTabs: React.FC<Props> = ({
  statuses,
  selectedStatus,
  orderTotal,
  handleTabChange,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const getStatusColor = (statusName: string) => {
    switch (statusName) {
      case "Planning":
        return { bg: "#fef3c7", color: "#92400e" };
      case "Opened":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "Accepted":
        return { bg: "#d1fae5", color: "#065f46" };
      case "Sending":
        return { bg: "#fef3c7", color: "#92400e" };
      case "Receiving":
        return { bg: "#e0e7ff", color: "#3730a3" };
      case "Revision":
        return { bg: "#f3e8ff", color: "#7c3aed" };
      case "Canceled":
        return { bg: "#fee2e2", color: "#991b1b" };
      default:
        return { bg: "#f3f4f6", color: "#374151" };
    }
  };

  return (
    <Box
      sx={{
        mb: 4,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={selectedStatus || null}
        onChange={handleTabChange}
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        dir={locale === LANG.AR ? "rtl" : "ltr"}
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderBottom: "1px solid #e5e7eb",
          width: "100%",
          "& .MuiTabs-indicator": {
            height: 3,
            background: "#18181b",
            borderRadius: "3px 3px 0 0",
            transition: "all 0.3s ease",
          },
          "& .MuiTab-root": {
            fontSize: isMobile ? "0.75rem" : isTablet ? "0.875rem" : "1rem",
            minWidth: isMobile ? "auto" : isTablet ? 100 : 140,
            padding: isMobile
              ? "12px 8px"
              : isTablet
              ? "16px 12px"
              : "20px 16px",
            fontWeight: 500,
            textTransform: "none",
            color: "#6b7280",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              color: "#374151",
              backgroundColor: "rgba(59, 130, 246, 0.04)",
            },
            "&.Mui-selected": {
              color: "#1f2937",
              fontWeight: 600,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "#18181b",
                borderRadius: "2px 2px 0 0",
              },
            },
          },
          "& .MuiTabs-scrollButtons": {
            color: "#6b7280",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(8px)",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            margin: "8px 4px",
            "&:hover": {
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              color: "#644980",
            },
            "&.Mui-disabled": {
              opacity: 0.3,
              color: "#9ca3af",
            },
          },
          "& .MuiTabs-scrollButtons.MuiButtonBase-root": {
            transform: locale === LANG.AR ? "scaleX(-1)" : "none",
          },
        }}
      >
        {/* All Tab */}
        <Tab
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexDirection: "column",
                minHeight: isMobile ? 40 : 50,
              }}
            >
              <Typography
                variant={isMobile ? "body2" : "body1"}
                sx={{
                  fontWeight: "inherit",
                  color: "inherit",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {t("general.All")}
              </Typography>
              <Chip
                label={orderTotal}
                sx={{
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  height: isMobile ? 20 : 24,
                  minWidth: isMobile ? 24 : 28,
                  border: "1px solid #d1d5db",
                  "& .MuiChip-label": {
                    padding: isMobile ? "0 6px" : "0 8px",
                  },
                }}
                size="small"
              />
            </Box>
          }
          value={null}
        />

        {/* Status Tabs */}
        {statuses.map((status) => {
          const statusColors = getStatusColor(status.name);
          return (
            <Tab
              key={status.name}
              value={status.name}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexDirection: "column",
                    minHeight: isMobile ? 40 : 50,
                  }}
                >
                  <Typography
                    variant={isMobile ? "body2" : "body1"}
                    sx={{
                      fontWeight: "inherit",
                      color: "inherit",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {status.label}
                  </Typography>
                  <Chip
                    label={status.total}
                    sx={{
                      backgroundColor: "#f3f4f6",
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                      height: isMobile ? 20 : 24,
                      minWidth: isMobile ? 24 : 28,
                      border: `1px solid ${statusColors.color}20`,
                      transition: "all 0.2s ease",
                      "& .MuiChip-label": {
                        padding: isMobile ? "0 6px" : "0 8px",
                      },
                      "&:hover": {
                        backgroundColor: `${statusColors.bg}dd`,
                        transform: "scale(1.05)",
                      },
                    }}
                    size="small"
                  />
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default StatusTabs;
